import { useMemo } from 'react';

import ArrowRightIcon from '@crehana/compass.icons.outline/ArrowRight';
import { AvatarGroup, type TAvatar } from '@crehana/compass.ui/avatar-group';

import { useCourseDetailTranslation } from '../../../hooks/useCourseDetailTranslation';
import { LeaderAssignContentButton } from '../../leader-assign-content-button/leader-assign-content-button';
import { useEnrollmentsByCourse } from '../hooks/use-enrollments-by-course';
import { EnrollmentsCurrentCourseDialog } from './leader-enrollments-course-dialog/leader-enrollments-course-dialog';

type TEnrollmentsCurrentCourseInfoProps = {
  courseId: string;
  courseTitle: string;
  organizationSlug: string;
};

export const EnrollmentsCurrentCourseInfo: React.FC<
  TEnrollmentsCurrentCourseInfoProps
> = ({ courseId, courseTitle, organizationSlug }) => {
  const { t } = useCourseDetailTranslation();
  const { enrollments } = useEnrollmentsByCourse({
    courseId: Number(courseId),
    pageSize: 104,
  });

  const avatarUsers = useMemo<TAvatar[]>(
    () =>
      enrollments.map(enrollment => ({
        src: enrollment.avatar,
        alt: enrollment.firstName,
        userName: `${enrollment.firstName} ${enrollment.lastName}`,
      })),
    [enrollments],
  );

  return (
    <>
      <p className="tw-font-body5 tw-text-neutral-light-700 tw-mb-1.6">
        {t('ENROLLMENTS_CURRENT_COURSE_INFO')}
      </p>

      <div className="tw-flex tw-items-end sm:tw-items-center tw-justify-start sm:tw-justify-between tw-gap-1 tw-mb-1.6">
        <div>
          <AvatarGroup
            label={t('ENROLLMENTS_CURRENT_COURSE_INFO_AVATAR_LABEL')}
            avatars={avatarUsers}
          />
        </div>
        <EnrollmentsCurrentCourseDialog
          courseId={courseId}
          courseTitle={courseTitle}
        >
          <div className="tw-flex tw-items-center tw-gap-0.4 tw-font-subtitle5 tw-text-primary-light-500 tw-mb-1.2 sm:tw-mb-0">
            {t('ENROLLMENTS_CURRENT_COURSE_INFO_BUTTON')}
            <ArrowRightIcon className="tw-size-2 tw-flex-shrink-0" size={20} />
          </div>
        </EnrollmentsCurrentCourseDialog>
      </div>

      <p className="tw-font-caption tw-text-info-600 tw-bg-info-100 tw-rounded-1.2 tw-p-1.2 tw-text-center">
        {t('ENROLLMENTS_CURRENT_COURSE_INFO_NOTE')}
      </p>

      <LeaderAssignContentButton
        className="tw-mt-2.4 md:tw-hidden"
        courseId={courseId}
        organizationSlug={organizationSlug}
      />
    </>
  );
};
